import { Info } from 'luxon';

import type { DayPattern, ShiftSchedule } from '@/types/template';

/**
 * Canonical Mon → Sun column order used by every preview surface. Centralised so
 * the visual preview, the shift modal's day chips, the staff-group view, and the
 * per-day grids all agree on the same column-key → day-of-week mapping.
 *
 * The `dayOfWeek` numbering matches the contract `ShiftSchedule` — **ISO 8601**:
 * 1 = Mon, …, 7 = Sun. The column array runs Mon → Sun.
 */
export const DAY_COLUMNS: ReadonlyArray<{ key: DayColumnKey; dayOfWeek: number }> = [
  { key: 'mon', dayOfWeek: 1 },
  { key: 'tue', dayOfWeek: 2 },
  { key: 'wed', dayOfWeek: 3 },
  { key: 'thu', dayOfWeek: 4 },
  { key: 'fri', dayOfWeek: 5 },
  { key: 'sat', dayOfWeek: 6 },
  { key: 'sun', dayOfWeek: 7 },
];

/**
 * ISO day-of-week (1 = Mon … 7 = Sun) → display column index (0 = Mon column,
 * 6 = Sun column). Use this whenever indexing a 7-cell Mon → Sun array.
 */
export function toDisplayDayIndex(dayOfWeek: number): number {
  return dayOfWeek - 1;
}

/**
 * Named day-pattern presets → their concrete ISO day-of-week list. The `custom`
 * pattern intentionally has no entry — by definition it's any day set that
 * doesn't match a preset.
 */
export const DAY_PATTERN_DAYS: Record<Exclude<DayPattern, 'custom'>, number[]> = {
  weekdays: [1, 2, 3, 4, 5],
  weekends: [6, 7],
  everyday: [1, 2, 3, 4, 5, 6, 7],
};

/**
 * Reverse-lookup: given a concrete ISO day-of-week list, find the preset it
 * matches (if any), falling back to `'custom'`. Input order doesn't matter.
 */
export function detectDayPattern(days: number[]): DayPattern {
  const sorted = [...days].sort((a, b) => a - b);
  for (const [pattern, presetDays] of Object.entries(DAY_PATTERN_DAYS) as Array<
    [Exclude<DayPattern, 'custom'>, number[]]
  >) {
    if (presetDays.length === sorted.length && presetDays.every((d, i) => d === sorted[i])) {
      return pattern;
    }
  }
  return 'custom';
}

/**
 * Expands a contract `ShiftSchedule` to its concrete ISO day-of-week list
 * (1 = Mon … 7 = Sun). Preset strings map via {@link DAY_PATTERN_DAYS}; a
 * `number[]` (the custom case) is returned sorted.
 */
export function scheduleToDays(schedule: ShiftSchedule): number[] {
  if (Array.isArray(schedule)) {
    return [...schedule].sort((a, b) => a - b);
  }
  return [...DAY_PATTERN_DAYS[schedule]];
}

/**
 * Collapses an ISO day-of-week list to a contract `ShiftSchedule`: a preset
 * string when the set matches one, otherwise the sorted `number[]` custom case.
 */
export function daysToSchedule(days: number[]): ShiftSchedule {
  const pattern = detectDayPattern(days);
  return pattern === 'custom' ? [...days].sort((a, b) => a - b) : pattern;
}

/**
 * Locale-aware short weekday label for an ISO `dayOfWeek` (1 = Mon … 7 = Sun).
 * Reads CLDR data via Luxon's `Info.weekdays`, a Mon-first ISO array.
 */
export function weekdayShortLabel(dayOfWeek: number, locale?: string): string {
  return Info.weekdays('short', { locale })[toDisplayDayIndex(dayOfWeek)] ?? '';
}

/**
 * Locale-aware single-letter weekday label for an ISO `dayOfWeek`
 * (1 = Mon … 7 = Sun), used for the compact round day toggles.
 */
export function weekdayNarrowLabel(dayOfWeek: number, locale?: string): string {
  return Info.weekdays('narrow', { locale })[toDisplayDayIndex(dayOfWeek)] ?? '';
}

/**
 * Three-letter English weekday label for an ISO `dayOfWeek`. Used **only** for
 * AI-prompt context where the model expects plain English regardless of locale —
 * never as a UI label (use `<DayLabel />` for that).
 */
export function englishDayLabel(dayOfWeek: number): string {
  return weekdayShortLabel(dayOfWeek, 'en');
}

export type DayColumnKey = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';
